import type { ProductPrice } from "@/api/product-api";
import { priceWithReferral } from "@/libs/referral/referral-pricing";

/**
 * NaverPay `<basePrice>` 산출 — **단일 출처**.
 *
 * NaverPay 주문형 V2.1 XML 에는 `discount`/`originalPrice` 필드가 없어 `<basePrice>`
 * 하나로만 가격을 전달한다. 그래서 **실판매가(originalAmount - discountAmount)** 를 보낸다.
 * `displayAmount`(정가/MSRP)를 쓰면 사용자가 정가로 결제된다.
 * — docs/naverpay-pricing.md, docs/order-item-pricing.md
 *
 * ⚠️ 이 산식이 표면마다 복사되면 표면마다 **보내는 단가가 갈린다**(가격 차이 자체는 네이버페이 주문 거절 사유가
 * 아니다 — `docs/naverpay-pricing.md` §두 응답의 가격 관계). 실제로 카트·PDP·액세서리 3곳에 복사돼 있었고, 미상 KRW 처리가
 * 서로 갈려 있었다(PDP 는 노출 안 함, 카트는 `?? 0` → `basePrice: 0` 라인 전송).
 * 새 표면을 만들 때 산식을 다시 쓰지 말고 이 함수를 부를 것.
 *
 * 서버(`api/naverpay/register.ts` 의 `resolveItemPrice`)는 이걸 쓰지 않는다 — 클라이언트
 * 값을 신뢰하지 않고 KRW 카탈로그에서 **권위 있게 재산출**하는 게 그쪽 역할이고, draft 용
 * original/display/discount 도 함께 만들어야 하기 때문이다. 산식(= original - discount)은
 * 동일하므로 값은 일치한다. 한쪽을 바꾸면 반드시 다른 쪽도 같이 볼 것.
 */
export function getNaverPayBasePrice(
  krwPrice: Pick<ProductPrice, "originalAmount" | "discountAmount"> | undefined,
): number | null {
  if (!krwPrice) return null;
  // 정가 0 = **미등록**(mgmt 가 0 을 채워 보낸다) — 「KRW 로 못 판다」와 같다. 실판매가 0(무료 라인)과
  // 다르다: 그건 정가가 있다. 공통 판정은 `ProductUtil.getPrice` 와 같은 `originalAmount > 0`.
  if (!(krwPrice.originalAmount > 0)) return null;
  const base = krwPrice.originalAmount - krwPrice.discountAmount;
  // 음수·NaN 만 막는다(카탈로그 이상). **0 은 통과시킨다** — 무료 라인이 실제로
  // 존재할 수 있고, 서버 resolveItemPrice 도 `Math.max(0, …)` 로 0 을 허용한다.
  // 여기서 0 을 막으면 무료 항목 하나 때문에 버튼이 통째로 사라져 과잉 차단이 된다.
  if (!Number.isFinite(base) || base < 0) return null;
  return base;
}

/**
 * 제품 가격 배열에서 KRW 행을 찾아 basePrice 를 낸다. 없으면 null.
 *
 * `referral` 을 주면 **화면 표시가**(referral 반영)를 낸다 — 서버 `register.ts` 는 이 값을 청구에 쓰지 않고
 * 「화면에 이게 보였다」는 증거로 대조만 한다(서버가 더 비싸면 409 `PriceChanged`). 그래서 화면과 **같은
 * percentile**(`useReferralDiscount` — 마운트 때 읽은 값)을 넘겨야 쿠키 만료를 잡는다. 산식은 서버와 같은
 * `priceWithReferral` 이다.
 */
export function findNaverPayBasePrice(
  prices: ProductPrice[] | undefined,
  referral?: { slug: string; referralPercentile: number },
): number | null {
  const krwPrice = prices?.find((p) => p.currency === "KRW");
  const base = getNaverPayBasePrice(krwPrice);
  if (base === null || !krwPrice || !referral) return base;
  return priceWithReferral({
    slug: referral.slug,
    krwPrice,
    referralPercentile: referral.referralPercentile,
  }).salePrice;
}

/**
 * 네이버페이 상품 상세 URL(`infoUrl`).
 *
 * ⚠️ 표면마다 인라인으로 적지 말 것 — PDP 버튼 2곳 · 카트 버튼 1곳에 같은 문자열이
 * 복사돼 있었고, 서버(`pages/api/naverpay/product-info.ts`)도 같은 경로를 만든다.
 * 구매 상세 경로가 바뀌면 네 곳을 함께 고쳐야 하는데, 하나라도 놓치면 네이버페이
 * 주문서에서 죽은 링크가 나간다.
 *
 * ⚠️ **경로는 같지만 origin 은 서버와 다르다.** 여기는 `window.location.origin` 이고
 * `product-info.ts` 는 `https://www.bhaptics.com` 을 하드코딩한다 — 즉 dev·staging 에서
 * 두 값이 갈린다. 기존부터 그랬고 의도인지 확인된 바 없다. 이 파일이 그 계약의 정본이
 * 됐으니 여기 적어둔다. 맞추려면 서버도 환경 기반 origin 을 써야 한다.
 *
 * 항상 `/ko` 다 — 네이버페이는 국내 결제 수단이다.
 */
export function buildNaverPayInfoUrl(slug: string): string {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return `${origin}/ko/shop/buy-tactsuit/${slug}/`;
}
